import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  EWFParamType,
  ISymbol,
  IWFParameter,
  IWorkFlow,
  IWorkFlowBaseInfo
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { createRef } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { SEARCH_DEBOUNCE_MS } from "../../../../shared/hooks/useDebouncedValue";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { IDataTypeRename } from "../../data-diagram/project-data-types-manager/data-type-references";
import { ProjectDataTypesManagerModal } from "../../data-diagram/project-data-types-manager/project-data-types-manager-modal";
import KuikaParametersTable, {
  KuikaParametersTableHandle
} from "../../shared/kuika-parameters-table/kuika-parameters-table";
import "./parameters-tab.scss";
import { applyWorkflowParameterRemoval, applyWorkflowParameterUpdate } from "./workflow-parameters";

/**
 * Stable identity for "this workflow has no parameters yet".
 *
 * `KuikaParametersTable` re-seeds its form whenever the `parameters` prop CHANGES IDENTITY
 * (`useEffect(..., [parameters])` -> `form.setFieldsValue`). A literal `?? []` allocates a fresh
 * array on every render, and this component is `withSD`, so it re-renders on any screen-design
 * change — autosave, collaboration, a selection change. On a process that has no parameters array
 * yet (every freshly created one) that turned each of those re-renders into a form re-seed, which
 * wipes a half-filled draft row: type a parameter name, let anything else in the designer tick,
 * and the name is silently gone. One shared empty array keeps the identity stable so the seed runs
 * only when the parameters themselves actually change.
 */
const NO_PARAMETERS: IWFParameter[] = [];

const SCOPE_ALL = "All";
const SCOPE_BUSINESS_OBJECT = "Business Object";
const SCOPE_INTERNAL = "Internal";

type ParameterScope = "all" | "businessObject" | "internal";

/**
 * The default a parameter falls back to when it carries none. The table seeds this same
 * shape into every row, so normalizing both sides of the change check keeps a legacy
 * parameter (saved before defaults existed) from reading as "changed" and needlessly
 * re-seeding its step bindings.
 */
const FIXED_DEFAULT_SYMBOL: ISymbol = {
  category: ESymbolCategory.fixed,
  label: "Fixed Value",
  description: ""
} as ISymbol;

/** The comparable projection of a parameter, so an unchanged row is not needlessly re-saved. */
interface ICanonicalParameter {
  name: string;
  dataType?: string;
  isObject: boolean;
  dataTypeName?: string;
  parameterType?: EWFParamType;
  isArray: boolean;
  isInput: boolean;
  isOutput: boolean;
  isBusinessObject: boolean;
  defaultValue: ISymbol;
}

export interface IParametersTabState {
  scope: string;
  /** The search input's live value; every keystroke echoes here. */
  search: string;
  /** The debounced copy of `search` that actually drives the table filter. */
  searchQuery: string;
  /** Where Save Changes stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isDataTypesModalOpen: boolean;
  /** True while a data-type mutation + its catalog reload is in flight (drives the button/dropdown spinners). */
  isDataTypesBusy: boolean;
}

export interface IParametersTabProps {}

class ParametersTab extends PureSdComponent<IParametersTabProps & IWithSDProps, IParametersTabState> {
  private tableRef = createRef<KuikaParametersTableHandle>();

  /**
   * The bar's save. The tab stays open after it, so the button is where the save is confirmed:
   * "Saving", then a held "Saved" (or "Try again"). `canStart` keeps a second submit out while
   * the ✓ is still up.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IParametersTabProps & IWithSDProps) {
    super(props);
    this.state = {
      scope: SCOPE_ALL,
      search: "",
      searchQuery: "",
      saveStatus: "idle",
      isDataTypesModalOpen: false,
      isDataTypesBusy: false
    };
  }

  componentWillUnmount(): void {
    this.applySearchQuery.cancel();
    this.saveStatus.dispose();
  }

  /** Debounced input→filter bridge; same pattern and interval as the process gallery. */
  private applySearchQuery = _.debounce((query: string): void => {
    this.setState({ searchQuery: query });
  }, SEARCH_DEBOUNCE_MS);

  private handleSearchChange = (value: string): void => {
    this.setState({ search: value });
    this.applySearchQuery(value);
  };

  private scopeForTab = (): ParameterScope => {
    if (this.state.scope === SCOPE_BUSINESS_OBJECT) return "businessObject";
    if (this.state.scope === SCOPE_INTERNAL) return "internal";
    return "all";
  };

  private canonical = (p: IWFParameter): ICanonicalParameter => ({
    name: (p.name ?? "").trim(),
    dataType: p.isObject ? undefined : p.dataType,
    isObject: !!p.isObject,
    dataTypeName: p.isObject ? p.dataTypeName : undefined,
    parameterType: p.parameterType,
    isArray: p.cardinality === ECardinality.Many,
    isInput: !!p.isInput,
    isOutput: !!p.isOutput,
    isBusinessObject: !!p.isBusinessObject,
    defaultValue: p.defaultValue ?? FIXED_DEFAULT_SYMBOL
  });

  private hasParameterChanged = (prev: IWFParameter, next: IWFParameter): boolean =>
    !_.isEqual(this.canonical(prev), this.canonical(next));

  // Called after the modal saves; keeps the "busy" flag up across the async catalog
  // reload so the Manage Data Types button and the in-cell dropdown show a spinner until
  // the fresh types are in.
  private reloadDataTypes = async (renames: IDataTypeRename[]): Promise<void> => {
    this.setState({ isDataTypesBusy: true });
    try {
      this.applyDataTypeRenames(renames);
      await this.props.sd.loadActionDefinitions?.();
    } finally {
      this.setState({ isDataTypesBusy: false });
    }
  };

  /**
   * Mirror a catalog rename into the in-memory workflows. The backend cascaded the rename into
   * every persisted workflow parameter, but this editor holds its own copies (`selectedWf`, the
   * workflow list) and saves whole documents — left stale, the next save would silently write the
   * old type name back over the cascade (KOA-18267). Parameters are patched in place (the tab's
   * established mutation style) and the table is re-seeded so its rows show the new name.
   */
  private applyDataTypeRenames = (renames: IDataTypeRename[]): void => {
    if (renames.length === 0) return;
    const renameByOldName: Map<string, string> = new Map<string, string>(
      renames.map((r: IDataTypeRename): [string, string] => [r.from, r.to])
    );
    const patchParameters = (parameters: IWFParameter[] | undefined): void => {
      (parameters ?? []).forEach((parameter: IWFParameter): void => {
        if (!parameter.isObject || !parameter.dataTypeName) return;
        const renamed: string | undefined = renameByOldName.get(parameter.dataTypeName);
        if (renamed) parameter.dataTypeName = renamed;
      });
    };
    patchParameters(this.props.sd.selectedWf?.parameters);
    (this.props.sd.workFlowList ?? []).forEach((workflow: IWorkFlowBaseInfo): void =>
      patchParameters(workflow.parameters)
    );
    this.tableRef.current?.reset();
  };

  /**
   * The whole-table commit. Diffs the submitted rows against the workflow's parameters and
   * routes each difference through the shared helpers so every step binding that references
   * a changed or removed parameter is re-seeded / stripped — losing that miscompiles silently.
   */
  handleSubmit = async (rows: IWFParameter[]): Promise<boolean> => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    if (!wf || !this.saveStatus.canStart) return false;
    if (!wf.parameters) wf.parameters = [];

    const existing: IWFParameter[] = wf.parameters;
    const existingById: Map<string, IWFParameter> = new Map(
      existing.map((p: IWFParameter): [string, IWFParameter] => [p.id, p])
    );
    const nextIds: Set<string> = new Set(rows.map((r: IWFParameter): string => r.id));
    const iterate = this.props.sd.iterateAllStepsRecursively;

    this.saveStatus.start();
    try {
      // Removals first — this splices wf.parameters, so iterate a copy.
      for (const p of [...existing]) {
        if (!nextIds.has(p.id)) applyWorkflowParameterRemoval(wf, p.id, iterate);
      }
      // Updates + adds.
      for (const row of rows) {
        const prev: IWFParameter | undefined = existingById.get(row.id);
        if (!prev) {
          wf.parameters.push(row);
        } else if (this.hasParameterChanged(prev, row)) {
          applyWorkflowParameterUpdate(wf, row.id, { ...prev, ...row }, iterate);
        }
      }

      const saved: boolean = await this.props.sd.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
      if (saved) {
        this.saveStatus.succeed();
        // No catalog reload here: saving parameters never mutates the Kuika Data Types catalog
        // (that only happens through the Manage Data Types modal, whose onSaved reloads it). Driving
        // `isDataTypesBusy` from a parameter save wedged the Manage Data Types button / in-cell
        // dropdown into a loading state for a reload the save never invalidated.
      } else {
        this.saveStatus.fail();
      }
      return saved;
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  };

  render() {
    return (
      <div className="kuika-process-parameters" data-testid="processParametersTab">
        <div className="kuika-process-parameters__card">
          <div className="kuika-process-parameters__header">
            <span className="kuika-process-parameters__title">
              <KuikaSvg name="components" width={16} height={16} color="var(--dark-11)" />
              <KuikaTextMedium>Process Parameters</KuikaTextMedium>
            </span>
            <span className="kuika-process-parameters__header-actions">
              {/* The table edits existing parameters in place, so the commit pair speaks
                  save-language — "Create" would promise a single new thing and mislabel
                  the dominant action. */}
              <KuikaButton
                type="text"
                uppercase
                onClick={(): void => this.tableRef.current?.reset()}
                data-testid="processParametersCancel"
              >
                Discard
              </KuikaButton>
              <KuikaButton
                type="primary"
                uppercase
                status={this.state.saveStatus}
                onClick={(): void => this.tableRef.current?.submit()}
                data-testid="processParametersCreate"
              >
                Save Changes
              </KuikaButton>
            </span>
          </div>

          <div className="kuika-process-parameters__toolbar">
            <KuikaTabs
              className="kuika-process-parameters__scope"
              size="lg"
              options={[SCOPE_ALL, SCOPE_BUSINESS_OBJECT, SCOPE_INTERNAL]}
              value={this.state.scope}
              onChange={(scope: string): void => this.setState({ scope })}
              dataTestId="processParametersScope"
            />
            <span className="kuika-process-parameters__toolbar-right">
              <KuikaButton
                type="secondary"
                size="md"
                uppercase
                loading={this.state.isDataTypesBusy}
                disabled={this.state.isDataTypesBusy}
                onClick={(): void => this.setState({ isDataTypesModalOpen: true })}
                data-testid="processParametersManageDataTypes"
              >
                <KuikaSvg name="settings" width={14} height={14} />
                Manage Data Types
              </KuikaButton>
              <KuikaInput
                className="kuika-process-parameters__search"
                size="md"
                placeholder="Search..."
                value={this.state.search}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                  this.handleSearchChange(String(value))
                }
                data-testid="processParametersSearch"
              />
            </span>
          </div>

          {/* <span className="kuika-process-parameters__section-label">Parameter Columns</span> */}

          <div className="kuika-process-parameters__grid">
            <KuikaParametersTable
              ref={this.tableRef}
              parameters={this.props.sd.selectedWf?.parameters ?? NO_PARAMETERS}
              types={this.props.sd.types}
              scope={this.scopeForTab()}
              search={this.state.searchQuery}
              onSubmit={this.handleSubmit}
              onManageDataTypes={(): void => this.setState({ isDataTypesModalOpen: true })}
              dataTypesLoading={this.state.isDataTypesBusy}
            />
          </div>
        </div>

        <ProjectDataTypesManagerModal
          isOpen={this.state.isDataTypesModalOpen}
          onClose={(): void => this.setState({ isDataTypesModalOpen: false })}
          onSaved={this.reloadDataTypes}
          workflows={this.props.sd.workFlowList}
        />
      </div>
    );
  }

  checkNeedRender = (): boolean => true;
}

const parametersTab = withSD<IParametersTabProps>(ParametersTab);
export { parametersTab as ParametersTab };
